import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Plus, Loader2, CheckCircle2, Circle, Trash2, CalendarClock, Flag, CalendarIcon } from "lucide-react";
import client from "@/api/client";
import { toast } from "sonner";
import { ar as arLocale } from "date-fns/locale";
import { format as formatDate } from "date-fns";

const PRIORITIES = ["low", "medium", "high"];
const PRIORITY_STYLES = {
  high: "border-rose-500/40 bg-rose-500/10 text-rose-600",
  medium: "border-amber-500/40 bg-amber-500/10 text-amber-600",
  low: "border-border bg-muted/40 text-muted-foreground",
};
const FILTERS = ["open", "done", "all"];

function dueMeta(due) {
  if (!due) return null;
  const today = new Date().toISOString().slice(0, 10);
  const day = due.slice(0, 10);
  if (day < today) return { tone: "text-rose-600", key: "overdue" };
  if (day === today) return { tone: "text-amber-600", key: "today" };
  return { tone: "text-muted-foreground", key: "upcoming" };
}

function TaskCard({ task, rooms, busy, onToggle, onDelete, t }) {
  const meta = dueMeta(task.due_at);
  const done = task.status === "done";
  return (
    <li
      className="group flex items-start gap-3 rounded-xl border border-border bg-card p-3 transition-all hover:border-primary/40 hover:shadow-sm"
      data-testid={`dashboard-task-${task.task_id}`}
    >
      <button
        type="button"
        onClick={() => onToggle(task)}
        disabled={busy === task.task_id}
        className={done ? "text-primary mt-0.5" : "text-muted-foreground hover:text-primary mt-0.5"}
        aria-label={done ? "Reopen task" : "Complete task"}
        data-testid={`dashboard-task-toggle-${task.task_id}`}
      >
        {busy === task.task_id ? <Loader2 className="h-5 w-5 animate-spin" /> : done ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className={`text-sm ${done ? "line-through text-muted-foreground" : "font-medium"}`}>{task.title}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <Badge variant="outline" className={`rounded-full text-[10px] px-2 py-0 ${PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.low}`}>
            <Flag className="h-2.5 w-2.5 me-1" />
            {t(`memberTasks.priority.${task.priority || "medium"}`, { defaultValue: task.priority || "medium" })}
          </Badge>
          {task.due_at && (
            <span className={`inline-flex items-center gap-1 text-[11px] font-mono ${meta?.tone}`} data-testid={`dashboard-task-due-${task.task_id}`}>
              <CalendarClock className="h-3 w-3" /> {task.due_at.slice(0, 10)}
              {meta?.key === "overdue" && (
                <span className="font-sans font-semibold" data-testid={`dashboard-task-overdue-${task.task_id}`}>
                  · {t("memberTasks.overdue", { defaultValue: "overdue" })}
                </span>
              )}
              {meta?.key === "today" && <span className="font-sans font-semibold">· {t("memberTasks.today", { defaultValue: "today" })}</span>}
            </span>
          )}
          {task.room_id && (
            <span className="text-[11px] text-muted-foreground truncate">
              {rooms.find((r) => r.room_id === task.room_id)?.company_name || task.room_id}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onDelete(task.task_id)}
        disabled={busy === task.task_id}
        className="text-muted-foreground hover:text-destructive opacity-60 group-hover:opacity-100 transition-opacity"
        aria-label="Delete task"
        data-testid={`dashboard-task-delete-${task.task_id}`}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  );
}

export function DashboardTasksTab({ data, reload }) {
  const { t, i18n } = useTranslation();
  const rooms = data?.rooms || [];
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [form, setForm] = useState({ title: "", due_at: "", priority: "medium", room_id: "" });
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/tasks");
      setTasks(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e?.preventDefault?.();
    const title = form.title.trim();
    if (!title) return;
    setCreating(true);
    try {
      await client.post("/tasks", {
        title,
        due_at: form.due_at || "",
        priority: form.priority,
        ...(form.room_id ? { room_id: form.room_id } : {}),
      });
      setForm({ title: "", due_at: "", priority: "medium", room_id: "" });
      await load();
      reload && reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (task) => {
    setBusy(task.task_id);
    try {
      await client.patch(`/tasks/${task.task_id}`, { status: task.status === "done" ? "open" : "done" });
      await load();
      reload && reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (task_id) => {
    setBusy(task_id);
    try {
      await client.delete(`/tasks/${task_id}`);
      await load();
      reload && reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(null);
    }
  };

  const { open, done, overdue } = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const o = tasks.filter((x) => x.status !== "done");
    return {
      open: o,
      done: tasks.filter((x) => x.status === "done"),
      overdue: o.filter((x) => x.due_at && x.due_at.slice(0, 10) < today),
    };
  }, [tasks]);

  const visible = filter === "open" ? open : filter === "done" ? done : tasks;

  return (
    <div className="space-y-5" data-testid="dashboard-tasks">
      <Card className="rounded-2xl border border-border p-4">
        <form onSubmit={add} className="grid gap-3 sm:grid-cols-[1fr_150px_130px_auto]">
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={t("dashboard.taskPlaceholder")}
            className="rounded-xl"
            data-testid="dashboard-task-input"
          />
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" className="rounded-xl h-10 justify-start gap-2 font-normal" data-testid="dashboard-task-due">
                <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className={form.due_at ? "font-mono text-xs" : "text-muted-foreground text-xs"}>
                  {form.due_at || t("memberTasks.pickDate", { defaultValue: "Due date" })}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" collisionPadding={12} className="w-auto p-0">
              <Calendar
                mode="single"
                selected={form.due_at ? new Date(`${form.due_at}T00:00:00`) : undefined}
                onSelect={(d) => setForm({ ...form, due_at: d ? formatDate(d, "yyyy-MM-dd") : "" })}
                locale={i18n.language === "ar" ? arLocale : undefined}
                dir={i18n.language === "ar" ? "rtl" : "ltr"}
                initialFocus
              />
              {form.due_at && (
                <div className="border-t border-border p-2">
                  <Button type="button" variant="ghost" size="sm" className="w-full text-xs" onClick={() => setForm({ ...form, due_at: "" })} data-testid="dashboard-task-due-clear">
                    {t("memberTasks.clearDate", { defaultValue: "Clear date" })}
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger className="rounded-xl h-10" data-testid="dashboard-task-priority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIORITIES.map((p) => (
                <SelectItem key={p} value={p} data-testid={`dashboard-task-priority-${p}`}>
                  {t(`memberTasks.priority.${p}`, { defaultValue: p })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="submit" className="btn-brand rounded-xl gap-2" disabled={creating || !form.title.trim()} data-testid="dashboard-task-add">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {t("dashboard.taskAdd")}
          </Button>
        </form>
        {rooms.length > 0 && (
          <div className="mt-3 w-full sm:w-[260px]">
            <Select value={form.room_id || "none"} onValueChange={(v) => setForm({ ...form, room_id: v === "none" ? "" : v })}>
              <SelectTrigger className="rounded-xl h-10" data-testid="dashboard-task-room">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("dashboard.taskNoRoom")}</SelectItem>
                {rooms.map((r) => (
                  <SelectItem key={r.room_id} value={r.room_id}>{r.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </Card>

      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-all ${
              filter === f
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-primary"
            }`}
            data-testid={`dashboard-task-filter-${f}`}
          >
            {t(`memberTasks.filter.${f}`, { defaultValue: f })}
            <span className="ms-1.5 font-mono opacity-80">
              {f === "open" ? open.length : f === "done" ? done.length : tasks.length}
            </span>
          </button>
        ))}
        {overdue.length > 0 && (
          <Badge variant="outline" className="rounded-full border-rose-500/40 bg-rose-500/10 text-rose-600 text-[11px]" data-testid="dashboard-task-overdue-badge">
            {overdue.length} {t("memberTasks.overdue", { defaultValue: "overdue" })}
          </Badge>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : visible.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="dashboard-tasks-empty">
          <p className="text-sm text-muted-foreground">{t("dashboard.taskEmpty")}</p>
        </Card>
      ) : (
        <ul className="grid gap-2.5 lg:grid-cols-2" data-testid="dashboard-tasks-list">
          {visible.map((task) => (
            <TaskCard key={task.task_id} task={task} rooms={rooms} busy={busy} onToggle={toggle} onDelete={remove} t={t} />
          ))}
        </ul>
      )}
    </div>
  );
}

export default DashboardTasksTab;
